/**
 * Character Creator — the casting + wardrobe + voice department, merged.
 *
 * Per the 2026-06-26 yap (characters.md / costume.md merge contract), a
 * character is now just a NAME + a free-text DESCRIPTION. Everything else
 * is derived:
 *
 *   1. LIST  — a stripped add form (name + description only) over a grid
 *              of casting trading-cards. The card avatar depicts the
 *              character in their PRIMARY costume (the primary look's
 *              reference image when set, else a deterministic pixel
 *              identicon).
 *
 *   2. DETAIL — a per-character editor with three tabs:
 *                 • Physical — the physical description, with INTELLIGENT
 *                   inline TAGGING of exactly four consistency attributes
 *                   (skin_tone via a colour picker, hairstyle, build,
 *                   age_range). Extraction is Claude-backed (debounced),
 *                   with a deterministic mock seam for tests.
 *                 • Wardrobe — the PRIMARY costume (primary clothing +
 *                   reference image, edited via the shared image editor).
 *                   Secondary looks are read-through from the Costume panel.
 *                 • Voice    — a voice profile (design-from-text OR clone an
 *                   existing voice) with a ▶ play that speaks "Hi, I'm
 *                   {name}" through the worker /tts route.
 *
 * The DETAIL editor is built as a reusable `CharacterDetailForm` so it can
 * be hosted either inline (clicking a card) OR inside the shared entity
 * edit-drawer (G6) when a character circle is clicked from the Script
 * Editor.
 *
 * There is intentionally NO emotion / state-of-mind, NO expression grid,
 * and NO AI image generation here.
 */

import "../styles/characters.css";

import { BasePanel } from "./BasePanel";
import {
  buildEmptyState,
  buildHelperParagraph,
  buildLabeledField,
  buildPixelBox,
  buildSectionLabel,
  buildStudioButton,
} from "./dom";
import { buildPixelIdenticonImage } from "../shared/pixelIdenticon";
import { openEntityDrawer, openImageEditor } from "../shared";
import type {
  CharacterAttributeKind,
  CharacterTaggedAttribute,
  CostumeLook,
  StudioCharacter,
  StudioStore,
} from "../StudioStore";

interface CharacterCreatorPanelOptions {
  store: StudioStore;
}

/**
 * Custom event other panels (Script Editor) dispatch to deep-link in.
 * `koko:open-character` navigates to the Characters panel (main.ts pairs
 * it with `panelManager.open`); `koko:edit-character` opens the SHARED
 * entity edit-drawer (G6) in place without navigating away — this is the
 * channel the Script Editor uses when a character circle is clicked.
 */
const OPEN_CHARACTER_EVENT = "koko:open-character";
const EDIT_CHARACTER_EVENT = "koko:edit-character";

/**
 * Result of an attribute extraction pass. `attributes` is the (≤4)
 * structured tagged attributes; `clothing` is an auto-derived primary
 * clothing description used to seed the character's primary costume.
 */
export interface CharacterExtractionResult {
  attributes: CharacterTaggedAttribute[];
  clothing?: string;
}

declare global {
  interface Window {
    /**
     * Test/demo seam: a deterministic attribute extractor. When present
     * it is used instead of the Claude-backed extractor so Playwright
     * stays green without hitting the worker.
     */
    __kokoCharacterExtractor?: (
      description: string,
    ) => CharacterExtractionResult | Promise<CharacterExtractionResult>;
  }
}

/** The four attributes, in display order, with their human labels. */
const ATTRIBUTE_ORDER: { kind: CharacterAttributeKind; label: string }[] = [
  { kind: "skin_tone", label: "skin tone" },
  { kind: "hairstyle", label: "hairstyle" },
  { kind: "build", label: "build" },
  { kind: "age_range", label: "age range" },
];

const DETAIL_TABS = ["physical", "wardrobe", "voice"] as const;
type DetailTab = (typeof DETAIL_TABS)[number];

const DEFAULT_SKIN_HEX = "#c8a07a";

export class CharacterCreatorPanel extends BasePanel {
  private readonly store: StudioStore;

  private nameInputEl: HTMLInputElement | null = null;
  private descriptionInputEl: HTMLTextAreaElement | null = null;

  private listViewEl: HTMLDivElement | null = null;
  private detailViewEl: HTMLDivElement | null = null;
  private characterListEl: HTMLDivElement | null = null;

  /** Non-null when the inline detail editor is open for that character. */
  private activeDetailCharacterId: string | null = null;
  /** The currently mounted inline detail form (for cleanup). */
  private inlineForm: CharacterDetailForm | null = null;
  /** Deep-link queued before the panel was shown; consumed on `onShown`. */
  private pendingDeepLinkId: string | null = null;

  private storeUnsubscribers: (() => void)[] = [];
  private readonly onOpenCharacterEvent = (event: Event) => {
    const id = (event as CustomEvent<{ characterId?: string }>).detail?.characterId;
    if (typeof id === "string" && id.length > 0) this.requestOpenCharacter(id);
  };
  private readonly onEditCharacterEvent = (event: Event) => {
    const id = (event as CustomEvent<{ characterId?: string }>).detail?.characterId;
    if (typeof id === "string" && id.length > 0) this.openInDrawer(id);
  };

  private previouslyRenderedIds = new Set<string>();
  private hasRenderedOnce = false;

  constructor(options: CharacterCreatorPanelOptions) {
    super();
    this.store = options.store;
  }

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");

    const listView = document.createElement("div");
    this.listViewEl = listView;
    listView.appendChild(this.buildAddForm());

    const listBox = buildPixelBox();
    listBox.fill.appendChild(buildSectionLabel("Cast"));
    const characterList = document.createElement("div");
    characterList.className = "studio-cast-grid";
    listBox.fill.appendChild(characterList);
    this.characterListEl = characterList;
    listView.appendChild(listBox.outer);

    const detailView = document.createElement("div");
    this.detailViewEl = detailView;
    detailView.style.display = "none";

    root.appendChild(listView);
    root.appendChild(detailView);
    return root;
  }

  private buildAddForm(): HTMLElement {
    const formBox = buildPixelBox();
    formBox.fill.appendChild(
      buildHelperParagraph(
        "Add each character with a name and a physical description. Koko auto-detects the four consistency attributes (skin tone, hairstyle, build, age) and derives a primary costume — open a card to refine, dress, and voice them.",
      ),
    );

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "studio-input";
    nameInput.placeholder = "e.g. Mei";
    nameInput.dataset.kokoAction = "character-name";
    this.nameInputEl = nameInput;

    const descriptionInput = document.createElement("textarea");
    descriptionInput.className = "studio-textarea";
    descriptionInput.placeholder =
      "Physical description — e.g. A lean woman in her early thirties with shoulder-length curly black hair and warm olive skin, in a paint-stained denim jacket.";
    descriptionInput.dataset.kokoAction = "character-description";
    this.descriptionInputEl = descriptionInput;

    formBox.fill.appendChild(buildLabeledField("Name", nameInput));
    formBox.fill.appendChild(buildLabeledField("Description", descriptionInput));

    const addButton = buildStudioButton("Add character", {
      variant: "primary",
      onClick: () => this.handleAddCharacter(),
    });
    addButton.dataset.kokoAction = "character-submit";
    const buttonRow = document.createElement("div");
    buttonRow.className = "studio-button-row";
    buttonRow.appendChild(addButton);
    formBox.fill.appendChild(buttonRow);

    // Enter in the name field jumps to the description for quick casting.
    nameInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        descriptionInput.focus();
      }
    });

    return formBox.outer;
  }

  protected onMounted(): void {
    this.storeUnsubscribers.push(
      this.store.subscribe("characters", () => this.handleCharactersChanged()),
    );
    window.addEventListener(OPEN_CHARACTER_EVENT, this.onOpenCharacterEvent);
    window.addEventListener(EDIT_CHARACTER_EVENT, this.onEditCharacterEvent);
    this.renderCharacterList();
  }

  protected onShown(): void {
    const hashId = this.readHashCharacterId();
    if (hashId) this.pendingDeepLinkId = hashId;

    if (this.pendingDeepLinkId) {
      const id = this.pendingDeepLinkId;
      this.pendingDeepLinkId = null;
      this.openDetail(id, { landed: true });
      return;
    }

    if (this.activeDetailCharacterId) {
      this.openDetail(this.activeDetailCharacterId);
      return;
    }

    this.showListView();
    this.renderCharacterList();
    if (this.nameInputEl) this.nameInputEl.focus();
  }

  protected onBeforeDestroy(): void {
    for (const unsub of this.storeUnsubscribers) unsub();
    this.storeUnsubscribers = [];
    window.removeEventListener(OPEN_CHARACTER_EVENT, this.onOpenCharacterEvent);
    window.removeEventListener(EDIT_CHARACTER_EVENT, this.onEditCharacterEvent);
    this.inlineForm?.destroy();
    this.inlineForm = null;
  }

  // ------------------------------------------------------------------
  // Deep-linking
  // ------------------------------------------------------------------

  /**
   * Open a character. If this panel is visible, open the inline detail
   * editor; otherwise (deep-linked from the Script Editor while another
   * surface is showing) open the SHARED entity edit-drawer (G6) so the
   * user never navigates away.
   */
  requestOpenCharacter(characterId: string): void {
    const exists = this.store
      .get("characters")
      .some((character) => character.id === characterId);
    if (!exists) return;
    if (this.rootElement && !this.rootElement.classList.contains("is-hidden")) {
      this.openDetail(characterId, { landed: true });
    } else {
      this.openInDrawer(characterId);
    }
  }

  private readHashCharacterId(): string | null {
    const match = /[#&]character=([a-zA-Z0-9_-]+)/.exec(window.location.hash);
    return match ? match[1] : null;
  }

  // ------------------------------------------------------------------
  // Add
  // ------------------------------------------------------------------

  private handleAddCharacter(): void {
    if (!this.nameInputEl || !this.descriptionInputEl) return;
    const name = this.nameInputEl.value.trim();
    const description = this.descriptionInputEl.value.trim();
    if (name.length === 0 || description.length === 0) return;

    const created = this.store.addCharacter(name, description);
    this.nameInputEl.value = "";
    this.descriptionInputEl.value = "";
    this.nameInputEl.focus();

    // Kick off a background extraction so the new card ships with its four
    // attributes + a derived primary costume already populated.
    void runExtractionInto(this.store, created.id, description);
  }

  private handleCharactersChanged(): void {
    if (this.activeDetailCharacterId) {
      const stillExists = this.store
        .get("characters")
        .some((character) => character.id === this.activeDetailCharacterId);
      if (!stillExists) {
        this.activeDetailCharacterId = null;
        this.inlineForm?.destroy();
        this.inlineForm = null;
        this.showListView();
        this.renderCharacterList();
      }
      // While the detail editor is open the form maintains itself via its
      // own subscription — don't rebuild it out from under the user.
      return;
    }
    this.renderCharacterList();
  }

  // ------------------------------------------------------------------
  // View switching
  // ------------------------------------------------------------------

  private showListView(): void {
    if (this.listViewEl) this.listViewEl.style.display = "";
    if (this.detailViewEl) {
      this.detailViewEl.replaceChildren();
      this.detailViewEl.style.display = "none";
    }
    this.activeDetailCharacterId = null;
    this.inlineForm?.destroy();
    this.inlineForm = null;
  }

  // ------------------------------------------------------------------
  // List view — casting trading cards
  // ------------------------------------------------------------------

  private renderCharacterList(): void {
    if (!this.characterListEl) return;
    this.characterListEl.replaceChildren();
    const characters = this.store.get("characters");
    if (characters.length === 0) {
      this.characterListEl.appendChild(
        buildEmptyState("No characters yet. Add one above, or ask Koko."),
      );
      this.previouslyRenderedIds = new Set();
      return;
    }
    const nextRenderedIds = new Set<string>();
    let staggerIndex = 0;
    for (const character of characters) {
      const isNew =
        this.hasRenderedOnce && !this.previouslyRenderedIds.has(character.id);
      const cardEl = this.buildCastCard(character, isNew);
      if (isNew) cardEl.style.setProperty("--stagger", String(staggerIndex++));
      this.characterListEl.appendChild(cardEl);
      nextRenderedIds.add(character.id);
    }
    this.previouslyRenderedIds = nextRenderedIds;
    this.hasRenderedOnce = true;
  }

  private buildCastCard(
    character: StudioCharacter,
    animateIn: boolean,
  ): HTMLElement {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "studio-cast-card";
    if (animateIn) card.classList.add("is-entering");
    card.dataset.cardId = character.id;
    card.dataset.kokoAction = "character-open";
    card.dataset.id = character.id;
    card.setAttribute("aria-label", `Open ${character.name}`);
    card.addEventListener("click", () => this.openDetail(character.id));

    const portrait = document.createElement("div");
    portrait.className = "studio-cast-card-portrait";
    portrait.appendChild(buildAvatar(this.store, character));

    const role = document.createElement("span");
    role.className = "studio-cast-card-role";
    role.textContent = "cast";
    portrait.appendChild(role);

    // Keep `.studio-card-title` alongside the new class so existing
    // selectors (sidebar dot tests, Koko choreography) keep resolving.
    const name = document.createElement("div");
    name.className = "studio-cast-card-name studio-card-title";
    name.textContent = character.name;

    const tagline = document.createElement("div");
    tagline.className = "studio-cast-card-tagline";
    tagline.textContent = character.description;

    const specs = document.createElement("div");
    specs.className = "studio-cast-card-specs";
    for (const { kind } of ATTRIBUTE_ORDER) {
      const attr = findAttribute(character, kind);
      if (!attr) continue;
      const chip = document.createElement("span");
      chip.className = `studio-cast-chip is-${kind}`;
      if (kind === "skin_tone") {
        const swatch = document.createElement("span");
        swatch.className = "studio-chip-swatch";
        swatch.style.background = normalizeHex(attr.value) ?? DEFAULT_SKIN_HEX;
        chip.appendChild(swatch);
        chip.appendChild(document.createTextNode(displayText(character, attr)));
      } else {
        chip.textContent = displayText(character, attr);
      }
      specs.appendChild(chip);
    }

    card.appendChild(portrait);
    card.appendChild(name);
    card.appendChild(tagline);
    card.appendChild(specs);
    return card;
  }

  // ------------------------------------------------------------------
  // Detail — inline + drawer
  // ------------------------------------------------------------------

  private openDetail(
    characterId: string,
    options: { landed?: boolean } = {},
  ): void {
    const character = this.store
      .get("characters")
      .find((entry) => entry.id === characterId);
    if (!character || !this.detailViewEl || !this.listViewEl) {
      this.showListView();
      return;
    }
    this.inlineForm?.destroy();
    this.activeDetailCharacterId = characterId;
    this.listViewEl.style.display = "none";
    this.detailViewEl.style.display = "";

    const form = new CharacterDetailForm(this.store, characterId, {
      chrome: "panel",
      landed: Boolean(options.landed),
      onBack: () => {
        this.showListView();
        this.renderCharacterList();
      },
      onDeleted: () => {
        this.showListView();
        this.renderCharacterList();
      },
    });
    this.inlineForm = form;
    this.detailViewEl.replaceChildren(form.getElement());
    this.detailViewEl.scrollIntoView({ block: "start", behavior: "auto" });
  }

  private openInDrawer(characterId: string): void {
    const character = this.store
      .get("characters")
      .find((entry) => entry.id === characterId);
    if (!character) return;
    const form = new CharacterDetailForm(this.store, characterId, {
      chrome: "drawer",
    });
    openEntityDrawer({
      title: `Edit ${character.name}`,
      subtitle: `character · ${character.id}`,
      render: (body) => body.appendChild(form.getElement()),
      onClose: () => form.destroy(),
    });
  }
}

// ====================================================================
// CharacterDetailForm — the reusable tabbed editor (inline + drawer)
// ====================================================================

interface CharacterDetailFormOptions {
  chrome: "panel" | "drawer";
  landed?: boolean;
  onBack?: () => void;
  onDeleted?: () => void;
}

/**
 * The per-character editor used both inline in the panel and inside the
 * shared entity drawer (G6). Holds stable input references and updates
 * its derived regions (tag preview, tag controls, avatar) in place on
 * store changes so it never clobbers a field the user is editing.
 */
export class CharacterDetailForm {
  private readonly store: StudioStore;
  private readonly characterId: string;
  private readonly options: CharacterDetailFormOptions;
  private readonly root: HTMLDivElement;

  private activeTab: DetailTab = "physical";
  private unsub: (() => void) | null = null;
  private extractTimer: number | null = null;
  private destroyed = false;

  // Stable element refs.
  private nameInput!: HTMLInputElement;
  private descInput!: HTMLTextAreaElement;
  private clothingInput!: HTMLTextAreaElement;
  private avatarHostEls: HTMLElement[] = [];
  private previewEl!: HTMLDivElement;
  private statusEl!: HTMLSpanElement;
  private tabBar!: HTMLElement;
  private readonly tagControls = new Map<
    CharacterAttributeKind,
    {
      row: HTMLDivElement;
      colorInput?: HTMLInputElement;
      textInput: HTMLInputElement;
      detectedLabel: HTMLSpanElement;
    }
  >();

  // Voice refs.
  private voiceDesignInput!: HTMLTextAreaElement;
  private voiceCloneInput!: HTMLInputElement;
  private voiceModeRadios: HTMLInputElement[] = [];
  private ttsPlayer: {
    speak: (t: string) => Promise<void>;
    stop: () => void;
  } | null = null;

  constructor(
    store: StudioStore,
    characterId: string,
    options: CharacterDetailFormOptions,
  ) {
    this.store = store;
    this.characterId = characterId;
    this.options = options;
    this.root = document.createElement("div");
    this.root.className = "studio-character-detail";
    this.root.dataset.characterId = characterId;
    if (options.landed) this.root.classList.add("is-landed");
    this.build();
    this.unsub = this.store.subscribe("characters", () => this.refresh());
    this.refresh();
  }

  getElement(): HTMLElement {
    return this.root;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.extractTimer !== null) window.clearTimeout(this.extractTimer);
    this.unsub?.();
    this.unsub = null;
    this.ttsPlayer?.stop();
  }

  private character(): StudioCharacter | null {
    return (
      this.store.get("characters").find((c) => c.id === this.characterId) ?? null
    );
  }

  // ---- Build (once) ----

  private build(): void {
    const character = this.character();
    if (!character) return;

    const box = buildPixelBox();
    const sheet = document.createElement("div");
    sheet.className = "studio-refsheet";

    sheet.appendChild(this.buildHeader(character));
    sheet.appendChild(this.buildTabBar());

    const physical = this.buildPhysicalTab(character);
    const wardrobe = this.buildWardrobeTab(character);
    const voice = this.buildVoiceTab(character);
    physical.dataset.tab = "physical";
    wardrobe.dataset.tab = "wardrobe";
    voice.dataset.tab = "voice";
    sheet.appendChild(physical);
    sheet.appendChild(wardrobe);
    sheet.appendChild(voice);

    sheet.appendChild(this.buildActions(character));

    box.fill.appendChild(sheet);
    this.root.appendChild(box.outer);
    this.applyActiveTab();
  }

  private buildHeader(character: StudioCharacter): HTMLElement {
    const topbar = document.createElement("div");
    topbar.className = "studio-refsheet-topbar";

    const idline = document.createElement("div");
    idline.className = "studio-refsheet-identity";

    const avatar = document.createElement("div");
    avatar.className = "studio-detail-avatar";
    avatar.appendChild(buildAvatar(this.store, character));
    this.avatarHostEls.push(avatar);

    const heading = document.createElement("div");
    heading.className = "studio-refsheet-heading";
    const eyebrow = document.createElement("div");
    eyebrow.className = "studio-refsheet-eyebrow";
    eyebrow.textContent = `character · ${character.id}`;

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "studio-input studio-refsheet-name-input";
    nameInput.value = character.name;
    nameInput.dataset.kokoAction = "character-edit-name";
    nameInput.dataset.id = character.id;
    nameInput.setAttribute("aria-label", "Character name");
    nameInput.addEventListener("input", () => {
      const next = nameInput.value.trim();
      if (next.length > 0) this.store.updateCharacter(character.id, { name: next });
    });
    this.nameInput = nameInput;

    heading.appendChild(eyebrow);
    heading.appendChild(nameInput);
    idline.appendChild(avatar);
    idline.appendChild(heading);
    topbar.appendChild(idline);

    if (this.options.chrome === "panel" && this.options.onBack) {
      const backButton = buildStudioButton("← Back to cast", {
        onClick: () => this.options.onBack?.(),
      });
      backButton.dataset.kokoAction = "character-sheet-back";
      topbar.appendChild(backButton);
    }
    return topbar;
  }

  private buildTabBar(): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "studio-tabbar";
    bar.setAttribute("role", "tablist");
    for (const tab of DETAIL_TABS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "studio-tab";
      btn.dataset.tabTarget = tab;
      btn.dataset.kokoAction = `character-tab-${tab}`;
      btn.setAttribute("role", "tab");
      btn.textContent = tab;
      btn.addEventListener("click", () => {
        this.activeTab = tab;
        this.applyActiveTab();
      });
      bar.appendChild(btn);
    }
    this.tabBar = bar;
    return bar;
  }

  private applyActiveTab(): void {
    for (const btn of Array.from(
      this.tabBar.querySelectorAll<HTMLButtonElement>(".studio-tab"),
    )) {
      btn.classList.toggle("is-active", btn.dataset.tabTarget === this.activeTab);
      btn.setAttribute(
        "aria-selected",
        btn.dataset.tabTarget === this.activeTab ? "true" : "false",
      );
    }
    for (const panel of Array.from(
      this.root.querySelectorAll<HTMLElement>("[data-tab]"),
    )) {
      panel.style.display = panel.dataset.tab === this.activeTab ? "" : "none";
    }
  }

  // ---- Physical tab (description + intelligent tagging) ----

  private buildPhysicalTab(character: StudioCharacter): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "studio-tabpanel";

    const descInput = document.createElement("textarea");
    descInput.className = "studio-textarea studio-detail-desc";
    descInput.value = character.description;
    descInput.dataset.kokoAction = "character-edit-description";
    descInput.dataset.id = character.id;
    descInput.setAttribute("aria-label", "Physical description");
    descInput.addEventListener("input", () => {
      this.store.updateCharacter(character.id, { description: descInput.value });
      this.scheduleExtraction(descInput.value);
    });
    this.descInput = descInput;
    wrap.appendChild(buildLabeledField("Physical description", descInput));

    // Status + highlighted preview.
    const previewHead = document.createElement("div");
    previewHead.className = "studio-tag-previewhead";
    const previewLabel = document.createElement("span");
    previewLabel.className = "studio-field-label";
    previewLabel.textContent = "Detected attributes";
    const status = document.createElement("span");
    status.className = "studio-tag-status";
    this.statusEl = status;
    previewHead.appendChild(previewLabel);
    previewHead.appendChild(status);
    wrap.appendChild(previewHead);

    const preview = document.createElement("div");
    preview.className = "studio-tag-preview";
    preview.dataset.kokoAction = "character-tag-preview";
    this.previewEl = preview;
    wrap.appendChild(preview);

    // The four stable tag controls.
    const controls = document.createElement("div");
    controls.className = "studio-tag-controls";
    for (const { kind, label } of ATTRIBUTE_ORDER) {
      controls.appendChild(this.buildTagControl(kind, label));
    }
    wrap.appendChild(controls);

    return wrap;
  }

  private buildTagControl(
    kind: CharacterAttributeKind,
    label: string,
  ): HTMLElement {
    const row = document.createElement("div");
    row.className = `studio-tag-row is-${kind}`;
    row.dataset.attr = kind;
    row.dataset.kokoAction = `character-tag-${kind}`;

    const head = document.createElement("div");
    head.className = "studio-tag-rowhead";
    const labelEl = document.createElement("span");
    labelEl.className = "studio-tag-label";
    labelEl.textContent = label;
    const detected = document.createElement("span");
    detected.className = "studio-tag-detected";
    head.appendChild(labelEl);
    head.appendChild(detected);
    row.appendChild(head);

    const controlWrap = document.createElement("div");
    controlWrap.className = "studio-tag-control";

    let colorInput: HTMLInputElement | undefined;
    const textInput = document.createElement("input");
    textInput.type = "text";
    textInput.className = "studio-input studio-tag-input";

    if (kind === "skin_tone") {
      colorInput = document.createElement("input");
      colorInput.type = "color";
      colorInput.className = "studio-tag-color";
      colorInput.dataset.kokoAction = "character-skin-color";
      colorInput.setAttribute("aria-label", "Skin tone colour");
      colorInput.addEventListener("input", () => {
        textInput.value = colorInput!.value;
        this.commitTag(kind, colorInput!.value);
      });
      controlWrap.appendChild(colorInput);
      textInput.placeholder = "#rrggbb or 'warm olive'";
      textInput.setAttribute("aria-label", "Skin tone value");
    } else {
      textInput.placeholder = `Describe ${label}…`;
      textInput.setAttribute("aria-label", `${label} value`);
    }

    textInput.addEventListener("input", () => {
      if (kind === "skin_tone" && colorInput) {
        const hex = normalizeHex(textInput.value);
        if (hex) colorInput.value = hex;
      }
      this.commitTag(kind, textInput.value.trim());
    });

    controlWrap.appendChild(textInput);
    row.appendChild(controlWrap);

    this.tagControls.set(kind, {
      row,
      colorInput,
      textInput,
      detectedLabel: detected,
    });
    return row;
  }

  /** Write/replace one attribute's value into the store. Empty removes it. */
  private commitTag(kind: CharacterAttributeKind, rawValue: string): void {
    const character = this.character();
    if (!character) return;
    const value =
      kind === "skin_tone"
        ? normalizeHex(rawValue) ?? rawValue.trim()
        : rawValue.trim();
    const existing = (character.taggedAttributes ?? []).filter(
      (a) => a.attribute !== kind,
    );
    const next: CharacterTaggedAttribute[] = [...existing];
    if (value.length > 0) {
      // Preserve any prior span so the inline highlight keeps its anchor.
      const prior = findAttribute(character, kind);
      next.push({ attribute: kind, value, span: prior?.span });
    }
    this.store.updateCharacter(character.id, {
      taggedAttributes: sortAttributes(next),
      ...flatPatchFor(kind, value, character),
    });
  }

  private scheduleExtraction(description: string): void {
    if (this.extractTimer !== null) window.clearTimeout(this.extractTimer);
    this.statusEl.textContent = "detecting…";
    this.statusEl.classList.add("is-busy");
    this.extractTimer = window.setTimeout(() => {
      this.extractTimer = null;
      void runExtractionInto(this.store, this.characterId, description).finally(
        () => {
          if (this.destroyed) return;
          this.statusEl.classList.remove("is-busy");
          const character = this.character();
          if (character) this.updateStatus(character);
        },
      );
    }, 450);
  }

  // ---- Wardrobe tab (primary costume) ----

  private buildWardrobeTab(character: StudioCharacter): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "studio-tabpanel";

    wrap.appendChild(
      buildHelperParagraph(
        "The PRIMARY costume the character ships with. Secondary looks (per-scene wardrobe changes) live in the Costume panel.",
      ),
    );

    // Primary costume image slot + Edit (shared image editor, G7).
    const looksRow = document.createElement("div");
    looksRow.className = "studio-primarylook";

    const imageFrame = document.createElement("div");
    imageFrame.className = "studio-primarylook-image";
    imageFrame.dataset.kokoAction = "character-primary-image";
    this.avatarHostEls.push(imageFrame);
    looksRow.appendChild(imageFrame);

    const looksBody = document.createElement("div");
    looksBody.className = "studio-primarylook-body";

    const clothingInput = document.createElement("textarea");
    clothingInput.className = "studio-textarea";
    clothingInput.value = primaryClothing(this.store, character);
    clothingInput.placeholder =
      "Primary clothing — e.g. paint-stained denim jacket over a grey tee, scuffed boots.";
    clothingInput.dataset.kokoAction = "character-primary-clothing";
    clothingInput.addEventListener("input", () => {
      this.setPrimaryClothing(clothingInput.value);
    });
    this.clothingInput = clothingInput;
    looksBody.appendChild(buildLabeledField("Primary clothing", clothingInput));

    const editButton = buildStudioButton("Edit image", {
      variant: "primary",
      onClick: () => this.openPrimaryImageEditor(),
    });
    editButton.dataset.kokoAction = "character-primary-image-edit";
    const genButton = buildStudioButton("Generate from description", {
      onClick: () => void this.generatePrimaryImage(),
    });
    genButton.dataset.kokoAction = "character-primary-image-gen";
    const btnRow = document.createElement("div");
    btnRow.className = "studio-button-row";
    btnRow.appendChild(editButton);
    btnRow.appendChild(genButton);
    looksBody.appendChild(btnRow);

    looksRow.appendChild(looksBody);
    wrap.appendChild(looksRow);

    // Secondary looks — read-through from the Costume panel.
    wrap.appendChild(buildSectionLabel("Secondary looks (from Costume)"));
    const secList = document.createElement("div");
    secList.className = "studio-secondary-looks";
    secList.dataset.role = "secondary-looks";
    wrap.appendChild(secList);

    return wrap;
  }

  private setPrimaryClothing(text: string): void {
    const character = this.character();
    if (!character) return;
    const looks = this.primaryLooks();
    const primary = looks[0] ?? { id: primaryLookId(character.id), note: "" };
    const nextPrimary: CostumeLook = { ...primary, note: text };
    const nextLooks = [nextPrimary, ...looks.slice(1)];
    this.store.setCostumeLooks(character.id, nextLooks);
    if (character.primaryLookId !== nextPrimary.id) {
      this.store.updateCharacter(character.id, { primaryLookId: nextPrimary.id });
    }
  }

  private primaryLooks(): CostumeLook[] {
    return this.store.get("costumeLooksByCharacterId")[this.characterId] ?? [];
  }

  private openPrimaryImageEditor(): void {
    const character = this.character();
    if (!character) return;
    const current =
      this.primaryLooks()[0]?.referenceImage ?? character.referenceImage ?? "";
    openImageEditor({
      image: current,
      title: `Primary costume · ${character.name}`,
      promptPlaceholder: "Describe a change to the primary costume…",
      onChange: (next) => this.setPrimaryImage(next),
      onDone: (final) => this.setPrimaryImage(final),
    });
  }

  private async generatePrimaryImage(): Promise<void> {
    const character = this.character();
    if (!character) return;
    const { runGeneration } = await import("../shared");
    const prompt = [character.description, primaryClothing(this.store, character)]
      .filter(Boolean)
      .join(" — ");
    const result = await runGeneration({
      kind: "image",
      prompt: `Character portrait: ${prompt}`,
      aspect: "1:1",
      seed: character.id,
    });
    if (result.url) this.setPrimaryImage(result.url);
  }

  private setPrimaryImage(image: string): void {
    const character = this.character();
    if (!character) return;
    const looks = this.primaryLooks();
    const primary = looks[0] ?? { id: primaryLookId(character.id), note: "" };
    const nextPrimary: CostumeLook = { ...primary, referenceImage: image || null };
    const nextLooks = [nextPrimary, ...looks.slice(1)];
    this.store.setCostumeLooks(character.id, nextLooks);
    this.store.updateCharacter(character.id, {
      primaryLookId: nextPrimary.id,
      referenceImage: image || null,
    });
  }

  // ---- Voice tab ----

  private buildVoiceTab(character: StudioCharacter): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "studio-tabpanel";

    wrap.appendChild(
      buildHelperParagraph(
        "This voice profile is the source of truth for the character's dialogue everywhere — script playback, final video, all of it.",
      ),
    );

    const profile = character.voiceProfile ?? {};
    const mode: "design" | "clone" = profile.clonedVoiceId ? "clone" : "design";

    const modeRow = document.createElement("div");
    modeRow.className = "studio-voice-modes";
    const designRadio = this.buildVoiceMode("design", "Design from text", mode);
    const cloneRadio = this.buildVoiceMode("clone", "Clone an existing voice", mode);
    modeRow.appendChild(designRadio.label);
    modeRow.appendChild(cloneRadio.label);
    this.voiceModeRadios = [designRadio.input, cloneRadio.input];
    wrap.appendChild(modeRow);

    const designInput = document.createElement("textarea");
    designInput.className = "studio-textarea";
    designInput.value = profile.design ?? "";
    designInput.placeholder =
      "Describe the voice — e.g. gravelly older man, slight rasp, unhurried, dry wit.";
    designInput.dataset.kokoAction = "character-voice-design";
    designInput.addEventListener("input", () => this.commitVoice());
    this.voiceDesignInput = designInput;
    const designField = buildLabeledField("Voice description", designInput);
    designField.dataset.voiceFor = "design";
    wrap.appendChild(designField);

    const cloneInput = document.createElement("input");
    cloneInput.type = "text";
    cloneInput.className = "studio-input";
    cloneInput.value = profile.clonedVoiceId ?? "";
    cloneInput.placeholder = "Paste a cloned/uploaded voice id…";
    cloneInput.dataset.kokoAction = "character-voice-clone";
    cloneInput.addEventListener("input", () => this.commitVoice());
    this.voiceCloneInput = cloneInput;
    const cloneField = buildLabeledField("Cloned voice id", cloneInput);
    cloneField.dataset.voiceFor = "clone";
    wrap.appendChild(cloneField);

    for (const radio of this.voiceModeRadios) {
      radio.addEventListener("change", () => {
        this.applyVoiceMode();
        this.commitVoice();
      });
    }

    const playButton = buildStudioButton(`▶ Hear "Hi, I'm ${character.name}"`, {
      variant: "primary",
      onClick: () => void this.playVoice(playButton),
    });
    playButton.dataset.kokoAction = "character-voice-play";
    const playRow = document.createElement("div");
    playRow.className = "studio-button-row";
    playRow.appendChild(playButton);
    wrap.appendChild(playRow);

    this.applyVoiceMode();
    return wrap;
  }

  private buildVoiceMode(
    value: "design" | "clone",
    label: string,
    current: "design" | "clone",
  ): { label: HTMLLabelElement; input: HTMLInputElement } {
    const wrap = document.createElement("label");
    wrap.className = "studio-voice-mode";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = `voice-mode-${this.characterId}`;
    input.value = value;
    input.checked = current === value;
    input.dataset.kokoAction = `character-voice-mode-${value}`;
    const span = document.createElement("span");
    span.textContent = label;
    wrap.appendChild(input);
    wrap.appendChild(span);
    return { label: wrap, input };
  }

  private currentVoiceMode(): "design" | "clone" {
    return this.voiceModeRadios.find((r) => r.checked)?.value === "clone"
      ? "clone"
      : "design";
  }

  private applyVoiceMode(): void {
    const mode = this.currentVoiceMode();
    for (const field of Array.from(
      this.root.querySelectorAll<HTMLElement>("[data-voice-for]"),
    )) {
      field.style.display = field.dataset.voiceFor === mode ? "" : "none";
    }
  }

  private commitVoice(): void {
    const character = this.character();
    if (!character) return;
    const mode = this.currentVoiceMode();
    const profile = {
      design: this.voiceDesignInput.value.trim() || undefined,
      clonedVoiceId:
        mode === "clone"
          ? this.voiceCloneInput.value.trim() || undefined
          : undefined,
      playbackUrl: character.voiceProfile?.playbackUrl ?? null,
    };
    this.store.updateCharacter(character.id, { voiceProfile: profile });
  }

  private async playVoice(button: HTMLButtonElement): Promise<void> {
    const character = this.character();
    if (!character) return;
    button.classList.add("is-playing");
    try {
      const player = await this.ensureTtsPlayer();
      player.stop();
      await player.speak(`Hi, I'm ${character.name}`);
    } catch (error) {
      console.warn("[studio] character voice preview failed:", error);
    } finally {
      button.classList.remove("is-playing");
    }
  }

  private async ensureTtsPlayer() {
    if (this.ttsPlayer) return this.ttsPlayer;
    const [{ TTSPlayer }, { DEFAULT_VOICE_KEY }] = await Promise.all([
      import("../../output/TTSPlayer"),
      import("../../output/voices"),
    ]);
    this.ttsPlayer = new TTSPlayer({
      workerBaseUrl: resolveWorkerBaseUrl(),
      getVoiceId: () => {
        const cloned = this.character()?.voiceProfile?.clonedVoiceId;
        return cloned && cloned.length > 0 ? cloned : DEFAULT_VOICE_KEY;
      },
      onPlaybackStart: () => {},
      onPlaybackEnd: () => {},
      onError: (error: Error) =>
        console.warn("[studio] character TTS error:", error),
    });
    return this.ttsPlayer;
  }

  // ---- Actions ----

  private buildActions(character: StudioCharacter): HTMLElement {
    const actions = document.createElement("div");
    actions.className = "studio-refsheet-actions";

    // Compatibility hooks for Koko choreography (G6 bridge clicks
    // character-edit then character-edit-save). The form auto-saves, so
    // these are effectively no-ops that keep the choreography contract.
    const editHook = buildStudioButton("Edit", {
      onClick: () => {
        this.activeTab = "physical";
        this.applyActiveTab();
        this.descInput.focus();
      },
    });
    editHook.dataset.kokoAction = "character-edit";
    editHook.dataset.id = character.id;
    editHook.classList.add("is-compat-hook");

    const saveHook = buildStudioButton("Save", {
      variant: "primary",
      onClick: () => {
        if (this.options.chrome === "panel") this.options.onBack?.();
      },
    });
    saveHook.dataset.kokoAction = "character-edit-save";
    saveHook.dataset.id = character.id;
    saveHook.classList.add("is-compat-hook");

    const deleteButton = buildStudioButton("Delete character", {
      variant: "danger",
      onClick: () => {
        this.store.deleteCharacter(character.id);
        this.options.onDeleted?.();
      },
    });
    deleteButton.dataset.kokoAction = "character-delete";
    deleteButton.dataset.id = character.id;

    actions.appendChild(editHook);
    actions.appendChild(saveHook);
    actions.appendChild(deleteButton);
    return actions;
  }

  // ---- Refresh (derived regions) ----

  private refresh(): void {
    if (this.destroyed) return;
    const character = this.character();
    if (!character) return;

    // Sync stable inputs only when not focused, so we never clobber typing.
    setIfNotFocused(this.nameInput, character.name);
    setIfNotFocused(this.descInput, character.description);
    setIfNotFocused(this.clothingInput, primaryClothing(this.store, character));

    // Avatars depict the primary costume.
    for (const host of this.avatarHostEls) {
      host.replaceChildren(buildAvatar(this.store, character));
    }

    this.renderTagPreview(character);
    this.renderTagControls(character);
    this.renderSecondaryLooks();
    this.updateStatus(character);
  }

  private updateStatus(character: StudioCharacter): void {
    if (this.statusEl.classList.contains("is-busy")) return;
    const count = ATTRIBUTE_ORDER.filter((a) =>
      findAttribute(character, a.kind),
    ).length;
    this.statusEl.textContent = `${count}/4 detected`;
  }

  private renderTagPreview(character: StudioCharacter): void {
    this.previewEl.replaceChildren();
    const text = character.description ?? "";
    if (text.trim().length === 0) {
      const empty = document.createElement("span");
      empty.className = "studio-tag-preview-empty";
      empty.textContent = "Type a physical description to detect attributes.";
      this.previewEl.appendChild(empty);
      return;
    }

    // Collect non-overlapping spans (skin tone uses its descriptive span).
    const spans: { start: number; end: number; kind: CharacterAttributeKind }[] = [];
    for (const { kind } of ATTRIBUTE_ORDER) {
      const attr = findAttribute(character, kind);
      if (!attr) continue;
      const span = resolveSpan(text, attr);
      if (span) spans.push({ ...span, kind });
    }
    spans.sort((a, b) => a.start - b.start);

    let cursor = 0;
    for (const span of spans) {
      if (span.start < cursor) continue; // skip overlaps
      if (span.start > cursor) {
        this.previewEl.appendChild(
          document.createTextNode(text.slice(cursor, span.start)),
        );
      }
      const mark = document.createElement("mark");
      mark.className = `studio-tag-hl is-${span.kind}`;
      mark.dataset.attr = span.kind;
      mark.textContent = text.slice(span.start, span.end);
      if (span.kind === "skin_tone") {
        const hex = normalizeHex(findAttribute(character, "skin_tone")?.value ?? "");
        if (hex) mark.style.setProperty("--tag-color", hex);
      }
      mark.addEventListener("click", () => this.focusTagControl(span.kind));
      this.previewEl.appendChild(mark);
      cursor = span.end;
    }
    if (cursor < text.length) {
      this.previewEl.appendChild(document.createTextNode(text.slice(cursor)));
    }
  }

  private focusTagControl(kind: CharacterAttributeKind): void {
    this.activeTab = "physical";
    this.applyActiveTab();
    const control = this.tagControls.get(kind);
    if (!control) return;
    control.row.classList.add("is-flash");
    window.setTimeout(() => control.row.classList.remove("is-flash"), 900);
    (control.colorInput ?? control.textInput).focus();
    control.row.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  private renderTagControls(character: StudioCharacter): void {
    for (const { kind } of ATTRIBUTE_ORDER) {
      const control = this.tagControls.get(kind);
      if (!control) continue;
      const attr = findAttribute(character, kind);
      const present = Boolean(attr);
      control.row.classList.toggle("is-detected", present);
      control.detectedLabel.textContent = present ? "detected" : "add tag";

      if (kind === "skin_tone" && control.colorInput) {
        const hex = normalizeHex(attr?.value ?? "") ?? DEFAULT_SKIN_HEX;
        setColorIfNotFocused(control.colorInput, hex);
        setIfNotFocused(control.textInput, displayText(character, attr) || "");
      } else {
        setIfNotFocused(control.textInput, attr?.value ?? "");
      }
    }
  }

  private renderSecondaryLooks(): void {
    const host = this.root.querySelector<HTMLElement>(
      '[data-role="secondary-looks"]',
    );
    if (!host) return;
    host.replaceChildren();
    const looks = this.primaryLooks().slice(1); // look[0] = primary
    if (looks.length === 0) {
      const empty = buildEmptyState(
        "No secondary looks yet — add per-scene wardrobe changes in the Costume panel.",
      );
      empty.classList.add("is-compact");
      host.appendChild(empty);
      return;
    }
    for (const look of looks) {
      const chip = document.createElement("div");
      chip.className = "studio-secondary-look";
      const note = document.createElement("span");
      note.textContent = look.note || "(untitled look)";
      chip.appendChild(note);
      host.appendChild(chip);
    }
  }
}

// ====================================================================
// Extraction
// ====================================================================

/**
 * Run an attribute + primary-clothing extraction for `description` and
 * write the results into the store for `characterId`. Uses (in order):
 * the test/demo mock seam, the Claude-backed extractor, then a local
 * deterministic heuristic. Existing user-edited values are preserved
 * unless the extractor produces a confident replacement.
 */
async function runExtractionInto(
  store: StudioStore,
  characterId: string,
  description: string,
): Promise<void> {
  const result = await extractAttributes(description);
  const character = store.get("characters").find((c) => c.id === characterId);
  if (!character) return;

  // Merge: keep any attribute the user already set that the extractor
  // didn't return, but let the extractor fill/refresh the rest.
  const byKind = new Map<CharacterAttributeKind, CharacterTaggedAttribute>();
  for (const attr of character.taggedAttributes ?? []) byKind.set(attr.attribute, attr);
  for (const attr of result.attributes) {
    if (!isAttributeKind(attr.attribute)) continue;
    byKind.set(attr.attribute, normalizeAttribute(attr, description));
  }
  const merged = sortAttributes(Array.from(byKind.values()));

  const patch: Parameters<StudioStore["updateCharacter"]>[1] = {
    taggedAttributes: merged,
  };
  // Mirror into the flat back-compat fields.
  for (const attr of merged) {
    Object.assign(patch, flatPatchFor(attr.attribute, attr.value, character));
  }
  store.updateCharacter(characterId, patch);

  // Seed the primary clothing / look if one isn't set yet.
  if (result.clothing && result.clothing.trim().length > 0) {
    const looks = store.get("costumeLooksByCharacterId")[characterId] ?? [];
    const hasPrimaryNote = (looks[0]?.note ?? "").trim().length > 0;
    if (!hasPrimaryNote) {
      const primary: CostumeLook = {
        id: looks[0]?.id ?? primaryLookId(characterId),
        note: result.clothing.trim(),
        referenceImage: looks[0]?.referenceImage ?? null,
      };
      store.setCostumeLooks(characterId, [primary, ...looks.slice(1)]);
      store.updateCharacter(characterId, { primaryLookId: primary.id });
    }
  }
}

async function extractAttributes(
  description: string,
): Promise<CharacterExtractionResult> {
  const hook = window.__kokoCharacterExtractor;
  if (typeof hook === "function") {
    try {
      return await hook(description);
    } catch (error) {
      console.warn("[studio] mock extractor failed; falling back:", error);
    }
  }
  const workerBaseUrl = resolveWorkerBaseUrl();
  if (workerBaseUrl) {
    try {
      const viaClaude = await extractViaClaude(description, workerBaseUrl);
      if (viaClaude) return viaClaude;
    } catch (error) {
      console.warn("[studio] Claude extractor failed; using heuristic:", error);
    }
  }
  return heuristicExtract(description);
}

const EXTRACTOR_SYSTEM = [
  "You extract structured visual-consistency attributes from a character's",
  "physical description for a film pre-production tool. Return ONLY minified",
  "JSON, no prose. Shape:",
  '{"attributes":[{"attribute":"skin_tone|hairstyle|build|age_range","value":"...","span":{"start":N,"end":N}}],"clothing":"..."}',
  "Rules: at most one of each attribute; NEVER emotion or state of mind.",
  "For skin_tone, value MUST be a hex colour (#rrggbb) approximating the tone,",
  "and span must cover the descriptive phrase in the text. For the others,",
  "value is the phrase and span covers it. 'clothing' is a short primary-",
  "costume description derived from the text (empty string if none).",
].join(" ");

async function extractViaClaude(
  description: string,
  workerBaseUrl: string,
): Promise<CharacterExtractionResult | null> {
  const response = await fetch(`${workerBaseUrl.replace(/\/$/, "")}/chat`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      model: "claude-3-5-haiku-20241022",
      max_tokens: 600,
      stream: false,
      system: EXTRACTOR_SYSTEM,
      messages: [{ role: "user", content: description }],
    }),
  });
  if (!response.ok) return null;
  const payload = (await response.json()) as {
    content?: Array<{ type?: string; text?: string }>;
  };
  const text = (payload.content ?? [])
    .filter((b) => b.type === "text")
    .map((b) => b.text ?? "")
    .join("");
  const json = extractFirstJsonObject(text);
  if (!json) return null;
  const parsed = JSON.parse(json) as CharacterExtractionResult;
  if (!Array.isArray(parsed.attributes)) return null;
  return parsed;
}

function extractFirstJsonObject(text: string): string | null {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) return null;
  return text.slice(start, end + 1);
}

// ---- Local deterministic heuristic (offline / fallback) ----

const SKIN_WORDS: { re: RegExp; hex: string }[] = [
  { re: /\b(?:very\s+)?(?:pale|fair)\b(?:[\w\s-]*?\bskin\b)?/i, hex: "#f1d9c4" },
  { re: /\blight\b[\w\s-]*?\bskin\b/i, hex: "#e8c4a0" },
  { re: /\b(?:warm\s+)?olive\b(?:[\w\s-]*?\bskin\b)?/i, hex: "#c8a578" },
  { re: /\b(?:tan|tanned|bronze[d]?)\b(?:[\w\s-]*?\bskin\b)?/i, hex: "#b07a4f" },
  { re: /\b(?:brown|copper)\b[\w\s-]*?\bskin\b/i, hex: "#8a5a3b" },
  { re: /\b(?:dark|deep|ebony)\b(?:[\w\s-]*?\bskin\b)?/i, hex: "#5c3a24" },
];

const BUILD_RE =
  /\b(slim|slender|lean|wiry|petite|athletic|muscular|stocky|broad[- ]shouldered|heavyset|husky|stout|plump|average build)\b/i;
const AGE_RE =
  /\b(child|teenager|teenage|teen|early\s+(?:twenties|thirties|forties|fifties)|late\s+(?:twenties|thirties|forties|fifties)|mid[- ](?:twenties|thirties|forties)|twenties|thirties|forties|fifties|sixties|young adult|middle[- ]aged|elderly|in (?:her|his|their) (?:twenties|thirties|forties|fifties|\d0s)|\d0s)\b/i;
const HAIR_RE = /\b([\w-]+(?:\s+[\w-]+){0,3}?\s+hair)\b/i;
const CLOTHING_RE =
  /\b([\w-]+(?:\s+[\w-]+){0,3}?\s+(?:jacket|coat|dress|shirt|tee|t-shirt|jeans|trousers|suit|hoodie|sweater|cardigan|gown|robe|uniform|boots|sneakers|scarf|hat|cap|glasses|skirt|blouse|vest|overalls|kimono|cloak))\b/i;

function heuristicExtract(description: string): CharacterExtractionResult {
  const attributes: CharacterTaggedAttribute[] = [];

  for (const { re, hex } of SKIN_WORDS) {
    const m = re.exec(description);
    if (m) {
      attributes.push({
        attribute: "skin_tone",
        value: hex,
        span: { start: m.index, end: m.index + m[0].length },
      });
      break;
    }
  }
  pushMatch(attributes, "hairstyle", HAIR_RE.exec(description));
  pushMatch(attributes, "build", BUILD_RE.exec(description));
  pushMatch(attributes, "age_range", AGE_RE.exec(description));

  const clothingMatch = CLOTHING_RE.exec(description);
  const clothing = clothingMatch ? clothingMatch[0].trim() : "";

  return { attributes: sortAttributes(attributes), clothing };
}

function pushMatch(
  out: CharacterTaggedAttribute[],
  kind: CharacterAttributeKind,
  match: RegExpExecArray | null,
): void {
  if (!match) return;
  const value = (match[1] ?? match[0]).trim();
  out.push({
    attribute: kind,
    value,
    span: { start: match.index, end: match.index + match[0].length },
  });
}

// ====================================================================
// Pure helpers
// ====================================================================

function isAttributeKind(value: string): value is CharacterAttributeKind {
  return (
    value === "skin_tone" ||
    value === "hairstyle" ||
    value === "build" ||
    value === "age_range"
  );
}

const ATTR_RANK: Record<CharacterAttributeKind, number> = {
  skin_tone: 0,
  hairstyle: 1,
  build: 2,
  age_range: 3,
};

function sortAttributes(
  attributes: CharacterTaggedAttribute[],
): CharacterTaggedAttribute[] {
  return [...attributes].sort(
    (a, b) => ATTR_RANK[a.attribute] - ATTR_RANK[b.attribute],
  );
}

function findAttribute(
  character: StudioCharacter,
  kind: CharacterAttributeKind,
): CharacterTaggedAttribute | undefined {
  return (character.taggedAttributes ?? []).find((a) => a.attribute === kind);
}

/** Clamp a raw extracted attribute's span to the description bounds. */
function normalizeAttribute(
  attr: CharacterTaggedAttribute,
  description: string,
): CharacterTaggedAttribute {
  let span = attr.span;
  if (
    span &&
    (span.start < 0 || span.end > description.length || span.start >= span.end)
  ) {
    span = undefined;
  }
  return { attribute: attr.attribute, value: attr.value, span };
}

/**
 * The human-readable phrase for an attribute. For most attributes the
 * value IS the phrase; for skin tone the value is a hex, so we show the
 * descriptive phrase recovered from the span (falling back to the hex).
 */
function displayText(
  character: StudioCharacter,
  attr: CharacterTaggedAttribute | undefined,
): string {
  if (!attr) return "";
  if (attr.attribute === "skin_tone") {
    if (attr.span) {
      const phrase = character.description.slice(attr.span.start, attr.span.end);
      if (phrase.trim().length > 0) return phrase.trim();
    }
    return attr.value;
  }
  return attr.value;
}

/** Resolve a usable highlight span for an attribute, locating it if absent. */
function resolveSpan(
  text: string,
  attr: CharacterTaggedAttribute,
): { start: number; end: number } | null {
  if (
    attr.span &&
    attr.span.start >= 0 &&
    attr.span.end <= text.length &&
    attr.span.start < attr.span.end
  ) {
    return attr.span;
  }
  // Skin tone values are hex colours — can't be located in prose.
  if (attr.attribute === "skin_tone") return null;
  if (attr.value.trim().length === 0) return null;
  const idx = text.toLowerCase().indexOf(attr.value.toLowerCase());
  if (idx === -1) return null;
  return { start: idx, end: idx + attr.value.length };
}

/** Map a structured attribute to the flat back-compat character field. */
function flatPatchFor(
  kind: CharacterAttributeKind,
  value: string,
  character: StudioCharacter,
): Partial<Pick<StudioCharacter, "skinTone" | "hair" | "build" | "ageRange">> {
  switch (kind) {
    case "skin_tone": {
      // Prefer the descriptive phrase for the flat (legacy) field.
      const attr = findAttribute(character, "skin_tone");
      const phrase = attr ? displayText(character, attr) : value;
      return { skinTone: value.length ? phrase || value : "" };
    }
    case "hairstyle":
      return { hair: value };
    case "build":
      return { build: value };
    case "age_range":
      return { ageRange: value };
    default:
      return {};
  }
}

function normalizeHex(value: string): string | null {
  const trimmed = value.trim();
  const m = /^#?([0-9a-fA-F]{6})$/.exec(trimmed);
  if (m) return `#${m[1].toLowerCase()}`;
  const short = /^#?([0-9a-fA-F]{3})$/.exec(trimmed);
  if (short) {
    const [r, g, b] = short[1].split("");
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return null;
}

/** Deterministic primary-look id for a character (look[0]). */
function primaryLookId(characterId: string): string {
  return `lk_primary_${characterId}`;
}

/** The primary clothing description (primary look note). */
function primaryClothing(store: StudioStore, character: StudioCharacter): string {
  const looks = store.get("costumeLooksByCharacterId")[character.id] ?? [];
  return looks[0]?.note ?? "";
}

/**
 * Build the character avatar — depicts the PRIMARY costume. Uses the
 * primary look's reference image when present; otherwise a deterministic
 * pixel identicon seeded by the character id (stable across the app).
 */
function buildAvatar(store: StudioStore, character: StudioCharacter): HTMLElement {
  const looks = store.get("costumeLooksByCharacterId")[character.id] ?? [];
  const image = looks[0]?.referenceImage ?? character.referenceImage ?? null;
  if (image) {
    const img = document.createElement("img");
    img.className = "studio-avatar-photo";
    img.src = image;
    img.alt = `${character.name} in primary costume`;
    return img;
  }
  return buildPixelIdenticonImage(character.id, {
    alt: `${character.name} avatar`,
  });
}

function setIfNotFocused(
  input: HTMLInputElement | HTMLTextAreaElement,
  value: string,
): void {
  if (document.activeElement === input) return;
  if (input.value !== value) input.value = value;
}

function setColorIfNotFocused(input: HTMLInputElement, hex: string): void {
  if (document.activeElement === input) return;
  if (input.value.toLowerCase() !== hex.toLowerCase()) input.value = hex;
}

/** Resolve the worker base URL the same way main.ts / other panels do. */
function resolveWorkerBaseUrl(): string {
  return (
    window.__KOKO_CONFIG__?.api ??
    document.getElementById("koko")?.getAttribute("api") ??
    ""
  );
}
